import { useEffect, useState } from 'react';
import type { AccessRequestInfo, CostReport, FeedbackList, StudioSummary } from '../../../shared/protocol.ts';
import { account } from '../lib/account.ts';
import { api } from '../lib/api.ts';
import { formatBytes } from '../lib/emitter.ts';
import { ACCESS_SIZES, ACCESS_USES, AppBar, Avatar, ConfirmDialog, Icon, useObservable, useTitle } from './common.tsx';

/** The signed-in person's name, email and sessions. */
export function AccountPage() {
  useObservable(account);
  useTitle('Account');
  const me = account.account;
  const [name, setName] = useState(me?.name ?? '');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [studios, setStudios] = useState<StudioSummary[] | null>(null);
  const [confirmEverywhere, setConfirmEverywhere] = useState(false);
  useEffect(() => {
    if (me) void api.myStudios().then(setStudios).catch(() => {});
  }, [me?.id]);
  useEffect(() => setName(me?.name ?? ''), [me?.name]);

  if (!account.checked) {
    return (
      <div className="page">
        <AppBar />
        <main className="account-page">
          <div className="card row muted">
            <span className="spinner" /> Loading…
          </div>
        </main>
      </div>
    );
  }
  if (!me) {
    return (
      <div className="page">
        <AppBar />
        <main className="account-page">
          <div className="card">
            <h2>You’re not signed in</h2>
            <p className="muted">Sign in with your email to see your account.</p>
            <a className="btn primary" href="/" style={{ alignSelf: 'flex-start' }}>
              Sign in
            </a>
          </div>
        </main>
      </div>
    );
  }

  const save = async () => {
    setError('');
    try {
      await account.rename(name);
      localStorage.setItem('takehouse.name', name.trim());
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const hosted = studios?.filter((s) => s.role === 'host') ?? [];
  const joined = studios?.filter((s) => s.role !== 'host') ?? [];
  const bytes = hosted.reduce((a, s) => a + s.totalBytes, 0);

  return (
    <div className="page">
      <AppBar />
      <main className="account-page">
        <div className="row" style={{ gap: 14 }}>
          <Avatar name={me.name || me.email} size={48} />
          <div className="col" style={{ gap: 2 }}>
            <span className="eyebrow">Your account</span>
            <h1 className="ellipsis">{me.name || me.email}</h1>
          </div>
        </div>

        <section className="card">
          <h2>Profile</h2>
          <label className="field">
            Name
            <div className="row" style={{ gap: 8 }}>
              <input value={name} placeholder="How others see you" onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void save()} />
              <button className="primary" onClick={() => void save()} disabled={name.trim() === (me.name ?? '')}>
                {saved ? <Icon name="check" size={15} /> : null} {saved ? 'Saved' : 'Save'}
              </button>
            </div>
            <span className="field-hint">Shown to others in studios you join. You can still change it before joining each time.</span>
          </label>
          <div className="field">
            Email
            <div className="kv-row">
              <strong>{me.email}</strong>
              <span className="faint small">Signed up {new Date(me.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            </div>
            <span className="field-hint">You sign in with this address. Signing in with a different one gives a separate account.</span>
          </div>
          {error && (
            <div className="alert bad">
              <Icon name="warn" size={16} /> {error}
            </div>
          )}
        </section>

        <section className="card">
          <h2>Studios</h2>
          <div className="kv-row">
            <span>
              {studios == null
                ? 'Loading…'
                : `${hosted.length} hosted${bytes ? ` · ${formatBytes(bytes)} recorded` : ''} · ${joined.length} joined as producer or guest`}
            </span>
            <a className="btn" href="/">
              Your studios
            </a>
          </div>
          {!me.canCreateStudios && (
            <span className="field-hint">
              Creating studios is by invitation while Wordstow is in pre-release.{' '}
              {me.accessRequested ? 'You’ve registered interest; we’ll email you.' : <a href="/#register">Register interest</a>}
            </span>
          )}
        </section>

        {me.isOperator && <FeedbackSummary />}
        {me.isOperator && <AccessRequests />}
        {me.isOperator && <RunningCosts />}

        <section className="card">
          <h2>Signing in</h2>
          <div className="kv-row">
            <span className="muted small">Only this browser</span>
            <button
              onClick={async () => {
                await account.signOut();
                location.href = '/';
              }}
            >
              Sign out
            </button>
          </div>
          <div className="kv-row">
            <span className="muted small">Every browser and device, for example after losing a laptop</span>
            <button className="danger" onClick={() => setConfirmEverywhere(true)}>
              Sign out everywhere
            </button>
          </div>
        </section>
      </main>
      {confirmEverywhere && (
        <ConfirmDialog
          title="Sign out everywhere?"
          confirmLabel="Sign out everywhere"
          onClose={() => setConfirmEverywhere(false)}
          onConfirm={async () => {
            await account.signOutEverywhere();
            location.href = '/';
          }}
        >
          <p>Every browser signed in as {me.email}, this one included, will need a new emailed code to sign in again.</p>
          <p className="muted small">Recordings and studios aren’t affected, and anyone in a call stays in it.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

/** How much feedback is waiting (operators only), with the way to the feedback page. */
function FeedbackSummary() {
  const [counts, setCounts] = useState<FeedbackList['counts'] | null>(null);
  useEffect(() => {
    api.feedback('new').then((d) => setCounts(d.counts), () => setCounts({}));
  }, []);
  const total = Object.values(counts ?? {}).reduce((a, n) => a + n, 0);
  return (
    <section className="card">
      <div className="kv-row">
        <h2>Feedback</h2>
        {!!counts?.new && <span className="pill accent">{counts.new} new</span>}
      </div>
      <div className="kv-row">
        <span className="muted small">{counts ? `${counts.action ?? 0} marked for action · ${total} in all` : 'Loading…'}</span>
        <a className="btn" href="/admin/feedback">
          Open feedback
        </a>
      </div>
    </section>
  );
}

const label = (all: [string, string][], id: string) => all.find(([k]) => k === id)?.[1] ?? id;
const STATUS = { pending: ['Waiting', 'accent'], approved: ['Approved', 'ok'], declined: ['Declined', ''] } as const;

/** People asking to create studios (operators only): approve, decline, or take access away again. */
function AccessRequests() {
  const [list, setList] = useState<AccessRequestInfo[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => {
    api.accessRequests().then(setList, (e) => setError((e as Error).message));
  }, []);
  const decide = async (email: string, decision: 'approve' | 'decline') => {
    setBusy(email);
    setError('');
    try {
      const r = await api.decideAccess(email, decision);
      setList((l) => l?.map((x) => (x.email === email ? r : x)) ?? null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const waiting = list?.filter((r) => r.status === 'pending').length ?? 0;
  return (
    <section className="card">
      <div className="kv-row">
        <h2>Studio access requests</h2>
        {waiting > 0 && <span className="pill accent">{waiting} waiting</span>}
      </div>
      {error && <div className="alert bad small">{error}</div>}
      {!list && !error && <span className="muted small">Loading…</span>}
      {list && !list.length && (
        <span className="muted small">
          None yet. People who sign in without access are asked what they’d record. To invite someone directly, send them {location.origin}/#register.
        </span>
      )}
      {list?.map((r) => {
        const [status, tone] = STATUS[r.status];
        return (
          <div key={r.email} className="request-row">
            <div className="col grow" style={{ gap: 3, minWidth: 0 }}>
              <div className="row" style={{ gap: 8 }}>
                <strong className="ellipsis">{r.name || r.email}</strong>
                <span className={`pill ${tone}`}>{status}</span>
              </div>
              <span className="muted small">
                {r.name ? `${r.email} · ` : ''}
                {label(ACCESS_USES, r.use)} · {label(ACCESS_SIZES, r.groupSize)} ·{' '}
                {new Date(r.requestedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
              </span>
              {r.note && <span className="small request-note">{r.note}</span>}
            </div>
            <div className="row" style={{ gap: 6 }}>
              {r.status !== 'approved' && (
                <button className="primary sm" disabled={busy === r.email} onClick={() => void decide(r.email, 'approve')}>
                  Approve
                </button>
              )}
              {r.status === 'pending' && (
                <button className="sm" disabled={busy === r.email} onClick={() => void decide(r.email, 'decline')}>
                  Decline
                </button>
              )}
              {r.status === 'approved' && (
                <button className="sm" disabled={busy === r.email} onClick={() => void decide(r.email, 'decline')}>
                  Remove access
                </button>
              )}
            </div>
          </div>
        );
      })}
      <span className="field-hint">
        Approving lets that address create studios straight away and emails them to say so. Declining sends nothing: they keep seeing that their
        request is in. Studios they already made aren’t affected by removing access.
      </span>
    </section>
  );
}

const usd = (n: number) => (n < 0.005 ? '$0.00' : `$${n.toFixed(2)}`);
const gb = (n: number) => (n < 1 ? `${Math.round(n * 1000)} MB` : `${n.toFixed(n < 10 ? 2 : 1)} GB`);
const count = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(2)} M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)} k` : String(n));

/** What the site costs to run on Cloudflare this month (operators only). */
function RunningCosts() {
  const [r, setR] = useState<CostReport | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api.costs().then(setR, (e) => setError((e as Error).message));
  }, []);
  const cf = r?.cloudflare;
  const month = r ? new Date(`${r.month}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' }) : '';
  return (
    <section className="card">
      <h2>Running costs</h2>
      {error && <div className="alert bad small">{error}</div>}
      {!r && !error && <span className="muted small">Loading…</span>}
      {r && (
        <>
          {r.cost && (
            <div className="kv-row">
              <span>
                Cloudflare, {month} so far: <strong>{usd(r.cost.total)}</strong>
              </span>
              <span className="muted small">about {usd(r.cost.projected)} for the whole month at this rate</span>
            </div>
          )}
          {cf && r.cost && (
            <div className="col small" style={{ gap: 4 }}>
              <div className="kv-row">
                <span className="muted">Storage: {gb(cf.storageGB)} in {count(cf.objects)} files (averaging {gb(cf.averageGB)} this month)</span>
                <span>{usd(r.cost.storage)}</span>
              </div>
              <div className="kv-row">
                <span className="muted">
                  Bucket operations: {count(cf.classA)} writes, {count(cf.classB)} reads
                </span>
                <span>{usd(r.cost.operations)}</span>
              </div>
              <div className="kv-row">
                <span className="muted">Call relay (TURN): {gb(cf.turnEgressGB)} sent</span>
                <span>{usd(r.cost.turn)}</span>
              </div>
            </div>
          )}
          {r.error && <div className="alert warn small">{r.error}. Showing what the database knows.</div>}
          <div className="col small" style={{ gap: 4 }}>
            <span className="muted">
              Recordings in the bucket: {gb(r.storedGB)}
              {r.longTermGB > 0 ? `, ${gb(r.longTermGB)} of it in long-term storage` : ''}
            </span>
            {r.studios.slice(0, 8).map((s) => (
              <div className="kv-row" key={s.id}>
                <span className="ellipsis">{s.name}</span>
                <span className="muted">
                  {gb(s.gb)} · {s.files} files
                </span>
              </div>
            ))}
          </div>
          <span className="field-hint">
            Estimated from Cloudflare’s usage figures at list prices, after the free monthly allowances (10 GB stored, 1 M writes, 10 M reads, 1 TB relayed).
            Railway and Resend bill separately. Updated every 10 minutes.
          </span>
        </>
      )}
    </section>
  );
}
